import posthog from 'posthog-js'
import { Dispatch } from 'redux'
import bugsnag from '../lib/bugsnag'
import {
    CLEAR_CURRENT_USER,
    ClearCurrentUserAction,
    REQUEST_USER,
    RequestUserAction,
    SET_CURRENT_PROFILE,
    SET_CURRENT_USER,
    SET_DATE_RANGE,
    SET_HIDDEN_COLUMNS,
    SET_NAV_OPEN,
    SET_SCREENSHOT_MODE,
    SetCurrentProfileAction,
    SetCurrentUserAction,
    SetDateRangeAction,
    SetHiddenColumnsAction,
    SetNavOpenAction,
    SetScreenshotModeAction,
} from '../constants/reducers/user'
import { State } from '../reducers'
import { Profile } from '../types/profile'
import { DateRange } from '../types/reducers/user'
import type { EntityId } from '../types/constants'
import { axiosGet } from '../util/api'

export const setCurrentUser = (user: Models.User): SetCurrentUserAction => ({
    type: SET_CURRENT_USER,
    user,
    receivedAt: Date.now(),
})

export const clearCurrentUser = (): ClearCurrentUserAction => {
    // Clear Bugsnag user context on logout
    bugsnag.setUser()

    return {
        type: CLEAR_CURRENT_USER,
        receivedAt: Date.now(),
    }
}

const requestUser = (): RequestUserAction => ({
    type: REQUEST_USER,
    receivedAt: Date.now(),
})

export function clearAndFetchUser() {
    return (dispatch: Dispatch<any>) => {
        dispatch(clearCurrentUser())
        dispatch(fetchUser())
    }
}

export function fetchUser() {
    return (dispatch: Dispatch<any>) => {
        dispatch(requestUser())
        axiosGet('api/v5/user', null, true)
            .then((res) => res.data)
            .then((user) => {
                dispatch(setCurrentUser(user))

                if (window['gist'] && !user?.is_admin && !user?.impersonating) {
                    window['gist'].identify(user.id, {
                        email: user.email,
                        name: user.name,
                        has_keys: !!user.keys,
                        profile_count: user?.keys?.profiles?.length,
                        ad_spend_30d:
                            user?.keys?.profiles
                                ?.map((p: Profile) => Number(p?.total_ad_spend_30_days_usd ?? 0))
                                .reduce((p: number, c: number) => p + c, 0) / 100,
                    })
                }

                if (!posthog._isIdentified()) {
                    posthog.identify(user.id.toString(), {
                        email: user.email,
                        name: user.name,
                    })
                }

                // Set Bugsnag user context
                bugsnag.setUser(user.id.toString(), user.email, user.name)
            })
            .catch((res) => {
                console.log('caught error', res)
                dispatch(clearCurrentUser())
            })
    }
}

export function refreshUser() {
    return (dispatch: Dispatch<any>, getState: () => State) => {
        const state = getState()
        // Only refresh if we aren't currently loading
        if (!state.user.fetching) {
            dispatch(fetchUser())
        }
    }
}

function shouldFetchUser(state: State): boolean {
    const user = state.user.current
    return !user && !state.user.fetching && !state.user.loaded
}

export function fetchUserIfNeeded() {
    return (dispatch: Dispatch<any>, getState: () => State) => {
        if (shouldFetchUser(getState())) {
            return dispatch(fetchUser())
        } else {
            return Promise.resolve()
        }
    }
}

export const setHiddenColumns = (name: string, columns: string[]): SetHiddenColumnsAction => ({
    type: SET_HIDDEN_COLUMNS,
    name,
    columns,
    receivedAt: Date.now(),
})

export const setCurrentProfile = (profileId: EntityId): SetCurrentProfileAction => ({
    type: SET_CURRENT_PROFILE,
    profile: profileId,
    receivedAt: Date.now(),
})

export const setScreenshotMode = (): SetScreenshotModeAction => ({
    type: SET_SCREENSHOT_MODE,
    receivedAt: Date.now(),
})

export const setDateRange = (dateRange: DateRange): SetDateRangeAction => ({
    type: SET_DATE_RANGE,
    dateRange: dateRange,
    receivedAt: Date.now(),
})

export const setNavOpen = (open: boolean): SetNavOpenAction => ({
    type: SET_NAV_OPEN,
    open,
    receivedAt: Date.now(),
})
